import { create } from 'zustand';
import type { EmailApi } from '@/shared/mock-data';
import type { ReceiptRecord } from '@/shared/backend-api';

export type { ReceiptRecord } from '@/shared/backend-api';

type ReceiptApi = Pick<EmailApi, 'getMessageReceipts'>;

interface ReceiptState {
  // Pixel-open events keyed by local message id. Records are ordered oldest →
  // newest (the backend sorts ascending by openedAt).
  byMessageId: Record<string, ReceiptRecord[]>;
  loading: Record<string, boolean>;
  loadReceipts: (api: ReceiptApi, messageId: string) => Promise<void>;
  getReceipts: (messageId: string) => ReceiptRecord[];
  isSeen: (messageId: string) => boolean;
  getOpenCount: (messageId: string) => number;
  getLastOpenedAt: (messageId: string) => Date | undefined;
  // Flat, most-recent-first list of every loaded open across all messages —
  // backs the "Recent Opens" feed (Superhuman). Each entry keeps its source
  // messageId so the feed can deep-link back to the conversation.
  getRecentOpens: (limit?: number) => RecentOpen[];
  reset: () => void;
}

export interface RecentOpen {
  messageId: string;
  openedAt: Date;
  location: string | null;
  userAgent: string | null;
}

export const useReceiptStore = create<ReceiptState>((set, get) => ({
  byMessageId: {},
  loading: {},

  async loadReceipts(api, messageId) {
    if (get().loading[messageId]) return;
    set((s) => ({ loading: { ...s.loading, [messageId]: true } }));
    try {
      const receipts = await api.getMessageReceipts(messageId);
      set((s) => ({
        byMessageId: { ...s.byMessageId, [messageId]: receipts },
        loading: { ...s.loading, [messageId]: false },
      }));
    } catch {
      set((s) => ({ loading: { ...s.loading, [messageId]: false } }));
    }
  },

  getReceipts(messageId) {
    return get().byMessageId[messageId] ?? [];
  },

  isSeen(messageId) {
    return (get().byMessageId[messageId]?.length ?? 0) > 0;
  },

  getOpenCount(messageId) {
    return get().byMessageId[messageId]?.length ?? 0;
  },

  getLastOpenedAt(messageId) {
    const receipts = get().byMessageId[messageId];
    if (!receipts || receipts.length === 0) return undefined;
    // F071: compute the max openedAt explicitly rather than trusting the backend's
    // documented ascending order via receipts[last] — the sibling getRecentOpens already
    // sorts defensively; this mirrors it. Behaviour-preserving while the backend order holds.
    return receipts.reduce(
      (max, r) => (r.openedAt.getTime() > max.getTime() ? r.openedAt : max),
      receipts[0]!.openedAt
    );
  },

  getRecentOpens(limit = 50) {
    const all: RecentOpen[] = [];
    for (const [messageId, receipts] of Object.entries(get().byMessageId)) {
      for (const r of receipts) {
        all.push({
          messageId,
          openedAt: r.openedAt,
          location: r.location,
          userAgent: r.userAgent,
        });
      }
    }
    all.sort((a, b) => b.openedAt.getTime() - a.openedAt.getTime());
    return all.slice(0, limit);
  },

  reset() {
    set({ byMessageId: {}, loading: {} });
  },
}));
